import { useEffect, useState } from "react";
import {
  AlertTriangle,
  ArrowLeft,
  Check,
  DollarSign,
  Loader2,
  RefreshCw,
  X,
} from "lucide-react";
import { apiv2 } from "../apiv2";
import { useAuth } from "../auth";

interface Props {
  onBack: () => void;
}

type SpendRow = {
  project_id: string;
  period: string;
  total_cost_usd: number;
  call_count: number;
  base_cap_usd: number;
  override_amount_usd: number;
  effective_cap_usd: number;
  remaining_usd: number;
  blocked: boolean;
};

function defaultPeriod(): string {
  // Calendar month, UTC — must match backend ``current_period()``.
  const d = new Date();
  const y = d.getUTCFullYear();
  const m = (d.getUTCMonth() + 1).toString().padStart(2, "0");
  return `${y}-${m}`;
}

function formatUSD(n: number): string {
  return `$${n.toFixed(2)}`;
}

function utilizationPct(row: SpendRow): number {
  if (row.effective_cap_usd <= 0) return 0;
  return Math.min(100, (row.total_cost_usd / row.effective_cap_usd) * 100);
}

function utilizationTone(pct: number): {
  bar: string;
  text: string;
  ring: string;
} {
  if (pct >= 100)
    return {
      bar: "bg-red-500",
      text: "text-red-400",
      ring: "border-red-500/30",
    };
  if (pct >= 80)
    return {
      bar: "bg-yellow-500",
      text: "text-yellow-400",
      ring: "border-yellow-500/30",
    };
  return {
    bar: "bg-brand-500",
    text: "text-brand-400",
    ring: "border-brand-500/20",
  };
}

export default function EpochSpendDashboard({ onBack }: Props) {
  const { isAdmin } = useAuth();
  const [rows, setRows] = useState<SpendRow[]>([]);
  const [period, setPeriod] = useState(defaultPeriod());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [overrideOpen, setOverrideOpen] = useState<SpendRow | null>(null);

  useEffect(() => {
    if (isAdmin) load();
  }, [period, isAdmin]);

  async function load() {
    setLoading(true);
    setError("");
    try {
      const data = await apiv2.listEpochSpend(period);
      // Sort: blocked first, then by utilization desc — surfaces hotspots
      const sorted = [...data].sort((a, b) => {
        if (a.blocked !== b.blocked) return a.blocked ? -1 : 1;
        return utilizationPct(b) - utilizationPct(a);
      });
      setRows(sorted);
    } catch (e: unknown) {
      setError((e as Error).message);
    } finally {
      setLoading(false);
    }
  }

  if (!isAdmin) {
    return (
      <div className="overflow-y-auto h-full custom-scrollbar relative z-10">
        <div className="max-w-3xl mx-auto px-6 py-10">
          <button
            onClick={onBack}
            className="flex items-center gap-1.5 text-white/30 hover:text-brand-500 text-xs mb-6 transition"
          >
            <ArrowLeft size={14} /> Back
          </button>
          <div className="p-4 bg-yellow-500/10 border border-yellow-500/20 rounded-xl text-sm text-yellow-400 flex items-center gap-2">
            <AlertTriangle size={16} />
            Admin-only page.
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="overflow-y-auto h-full custom-scrollbar relative z-10">
      <div className="max-w-5xl mx-auto px-6 py-10">
        <button
          onClick={onBack}
          className="flex items-center gap-1.5 text-white/30 hover:text-brand-500 text-xs mb-6 transition"
        >
          <ArrowLeft size={14} /> Back
        </button>

        <div className="flex items-center gap-3 mb-8">
          <div className="w-10 h-10 rounded-xl bg-brand-500/10 border border-brand-500/20 flex items-center justify-center">
            <DollarSign size={20} className="text-brand-500" />
          </div>
          <div>
            <h1 className="text-xl font-bold">Epoch Gateway Spend</h1>
            <p className="text-xs text-white/30">
              Per-project monthly LLM spend through the Deloitte Epoch gateway.
            </p>
          </div>
        </div>

        {/* Period + refresh */}
        <div className="flex items-center justify-between mb-6 gap-3">
          <div className="flex items-center gap-2">
            <label className="text-xs text-white/40">Period</label>
            <input
              data-testid="epoch-spend-period"
              type="text"
              value={period}
              onChange={(e) => setPeriod(e.target.value)}
              placeholder="YYYY-MM"
              className="w-28 bg-white/5 border border-white/10 rounded-lg px-3 py-1.5 text-sm text-white focus:border-brand-500/50 focus:outline-none font-mono"
            />
            <button
              onClick={() => setPeriod(defaultPeriod())}
              className="text-[11px] text-white/30 hover:text-white/60 transition"
            >
              current
            </button>
          </div>
          <button
            onClick={load}
            disabled={loading}
            className="px-3 py-1.5 bg-white/5 border border-white/10 text-white/60 rounded-lg text-xs hover:border-brand-500/30 hover:text-white transition flex items-center gap-2"
          >
            {loading ? (
              <Loader2 size={12} className="animate-spin" />
            ) : (
              <RefreshCw size={12} />
            )}
            Refresh
          </button>
        </div>

        {error && (
          <div className="mb-4 p-3 bg-red-500/10 border border-red-500/20 rounded-xl text-sm text-red-400">
            {error}
          </div>
        )}

        {/* Table */}
        <div className="bg-white/[0.02] border border-white/5 rounded-2xl overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-white/[0.03]">
              <tr className="text-left text-[11px] uppercase tracking-wider text-white/40">
                <th className="px-4 py-3">Project</th>
                <th className="px-4 py-3 text-right">Spend</th>
                <th className="px-4 py-3 text-right">Cap</th>
                <th className="px-4 py-3 w-1/3">Utilization</th>
                <th className="px-4 py-3 text-right">Calls</th>
                <th className="px-4 py-3 text-right">Status</th>
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {!loading && rows.length === 0 && (
                <tr>
                  <td
                    colSpan={7}
                    className="px-4 py-12 text-center text-xs text-white/30"
                  >
                    No Epoch calls recorded for {period} yet.
                  </td>
                </tr>
              )}
              {rows.map((row) => {
                const pct = utilizationPct(row);
                const tone = utilizationTone(pct);
                return (
                  <tr
                    key={row.project_id}
                    className="border-t border-white/5 hover:bg-white/[0.02] transition"
                  >
                    <td className="px-4 py-3 font-mono text-xs text-white/70">
                      {row.project_id.slice(0, 8)}…
                    </td>
                    <td className="px-4 py-3 text-right tabular-nums">
                      {formatUSD(row.total_cost_usd)}
                    </td>
                    <td className="px-4 py-3 text-right tabular-nums text-white/50">
                      {formatUSD(row.effective_cap_usd)}
                      {row.override_amount_usd !== 0 && (
                        <span className="text-[11px] text-brand-400 ml-1">
                          (+{formatUSD(row.override_amount_usd)})
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <div className="flex-1 h-1.5 bg-white/5 rounded-full overflow-hidden">
                          <div
                            className={`h-full ${tone.bar} transition-all`}
                            style={{ width: `${pct}%` }}
                          />
                        </div>
                        <span
                          className={`text-[11px] tabular-nums ${tone.text} w-10 text-right`}
                        >
                          {pct.toFixed(0)}%
                        </span>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-right tabular-nums text-white/50">
                      {row.call_count}
                    </td>
                    <td className="px-4 py-3 text-right">
                      {row.blocked ? (
                        <span className="inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-full bg-red-500/10 text-red-400 border border-red-500/20">
                          <AlertTriangle size={10} /> Blocked
                        </span>
                      ) : pct >= 80 ? (
                        <span className="inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-full bg-yellow-500/10 text-yellow-400 border border-yellow-500/20">
                          Warning
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-full bg-green-500/10 text-green-400 border border-green-500/20">
                          OK
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <button
                        data-testid={`override-btn-${row.project_id}`}
                        onClick={() => setOverrideOpen(row)}
                        className="text-[11px] text-white/40 hover:text-brand-400 transition"
                      >
                        Override
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        <p className="text-[11px] text-white/20 mt-4">
          Base cap is $1,000/project/month. Override raises (or revokes) the
          effective cap on top of the base. All overrides are audited with admin
          id, timestamp, and reason on the rollup row.
        </p>

        {overrideOpen && (
          <OverrideModal
            row={overrideOpen}
            onClose={() => setOverrideOpen(null)}
            onSaved={(updated) => {
              setRows((current) =>
                current.map((r) =>
                  r.project_id === updated.project_id ? updated : r,
                ),
              );
              setOverrideOpen(null);
            }}
          />
        )}
      </div>
    </div>
  );
}

interface ModalProps {
  row: SpendRow;
  onClose: () => void;
  onSaved: (updated: SpendRow) => void;
}

function OverrideModal({ row, onClose, onSaved }: ModalProps) {
  const [amount, setAmount] = useState(row.override_amount_usd);
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  async function handleSave() {
    if (!reason.trim()) {
      setError("Reason is required for the audit trail.");
      return;
    }
    setSaving(true);
    setError("");
    try {
      const updated = await apiv2.setEpochSpendOverride(row.project_id, {
        amount_usd: amount,
        reason,
      });
      onSaved(updated);
    } catch (e: unknown) {
      setError((e as Error).message);
    } finally {
      setSaving(false);
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div
        className="bg-zinc-900 border border-white/10 rounded-2xl max-w-md w-full p-6"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-sm font-semibold">Override Monthly Cap</h2>
          <button
            onClick={onClose}
            className="text-white/30 hover:text-white/60"
          >
            <X size={16} />
          </button>
        </div>

        <p className="text-xs text-white/50 mb-4 font-mono">
          Project {row.project_id} · Period {row.period}
        </p>

        <div className="space-y-4">
          <div>
            <label className="block text-xs text-white/40 mb-1">
              Override amount (USD){" "}
              <span className="text-white/20">
                — additive to base ${row.base_cap_usd}. Set 0 to revoke.
              </span>
            </label>
            <input
              data-testid="override-amount"
              type="number"
              min={0}
              step={50}
              value={amount}
              onChange={(e) => setAmount(parseFloat(e.target.value) || 0)}
              className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-2.5 text-sm text-white focus:border-brand-500/50 focus:outline-none tabular-nums"
            />
            <p className="text-[11px] text-white/30 mt-1">
              Effective cap will be {formatUSD(row.base_cap_usd + amount)}.
            </p>
          </div>

          <div>
            <label className="block text-xs text-white/40 mb-1">
              Reason{" "}
              <span className="text-white/20">
                (required — ticket id or short justification)
              </span>
            </label>
            <textarea
              data-testid="override-reason"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={3}
              placeholder="ZTD-1234 — extending for client demo on 2026-05-20"
              className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-2.5 text-sm text-white placeholder:text-white/20 focus:border-brand-500/50 focus:outline-none"
            />
          </div>

          {error && (
            <div className="p-3 bg-red-500/10 border border-red-500/20 rounded-xl text-xs text-red-400">
              {error}
            </div>
          )}

          <div className="flex items-center gap-2 pt-2">
            <button
              data-testid="override-save"
              onClick={handleSave}
              disabled={saving}
              className="px-5 py-2 bg-gradient-to-r from-brand-500 to-green-500 text-black font-semibold rounded-xl text-xs hover:brightness-110 transition disabled:opacity-50 flex items-center gap-2"
            >
              {saving ? (
                <Loader2 size={12} className="animate-spin" />
              ) : (
                <Check size={12} />
              )}
              Apply override
            </button>
            <button
              onClick={onClose}
              className="px-4 py-2 bg-white/5 border border-white/10 text-white/60 rounded-xl text-xs hover:border-white/20 hover:text-white transition"
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
